import * as React from "react";
import RouterLink from "next/link";
import type { MapPortalData } from "~mangomap-ui/utils/mock-map-portal-data";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CalendarBlank as CalendarIcon } from "@phosphor-icons/react/CalendarBlank";
import { MapTrifold as MapIcon } from "@phosphor-icons/react/MapTrifold";
import { User as CreatorIcon } from "@phosphor-icons/react/User";
import { Users } from "@phosphor-icons/react/Users";
import { useTranslation } from "react-i18next";

import { AccessModeIndicator } from "../access-mode-indicator";
import { useBulkSelectionOptional } from "../bulk-selection-provider";
import { MapCardMenu } from "../map-card-menu";
import { MapAccessSettingsIndicators } from "../map-access-settings-indicators";

export interface MapListProps {
	maps: MapPortalData[];
	onMapClick?: (event: { map: MapPortalData }) => void;
	onMapDuplicate?: (event: { map: MapPortalData }) => void;
	onMapDelete?: (event: { map: MapPortalData }) => void;
	loading?: boolean;
	showStats?: boolean;
	canDelete?: (map: MapPortalData) => boolean;
}

export interface MapListItemProps {
	map: MapPortalData;
	href?: string;
	onClick?: (event: { map: MapPortalData }) => void;
	onDuplicate?: (event: { map: MapPortalData }) => void;
	onDelete?: (event: { map: MapPortalData }) => void;
	loading?: boolean;
	showStats?: boolean;
	canDelete?: boolean;
}

export function MapListItem({
	map,
	href,
	onClick,
	onDuplicate,
	onDelete,
	loading = false,
	showStats: _showStats = true,
	canDelete = false,
}: MapListItemProps): React.JSX.Element {
	const bulkSelection = useBulkSelectionOptional();

	// Simplified permission logic
	const canEdit = canDelete; // For now, edit and delete permissions are the same
	const showActions = canEdit || canDelete;

	const handleClick = () => {
		onClick?.({ map });
	};

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		if (bulkSelection?.canSelectMap(map)) {
			bulkSelection.toggleMapSelection(map.id);
		}
	};

	const handleCheckboxKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			event.stopPropagation();
			if (bulkSelection?.canSelectMap(map)) {
				bulkSelection.toggleMapSelection(map.id);
			}
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatDateShort = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
		});
	};

	const formatOwnerShort = (owner: string) => {
		return owner.split(" ")[0];
	};

	const metadataChipSx = {
		height: 24,
		fontSize: "0.75rem",
	};

	const fallbackImage = "/app-assets/map-placeholder.png";

	return (
		<Card
			sx={{
				cursor: loading ? "default" : "pointer",
				transition: "all .2s ease-in-out",
				"&:hover": loading
					? {}
					: {
							bgcolor: "var(--mui-palette-action-hover)",
							boxShadow: "var(--mui-shadows-2)",
							"& .selection-checkbox": {
								opacity: 1,
							},
						},
				opacity: loading ? 0.7 : 1,
				bgcolor: bulkSelection?.isSelectionMode
					? "var(--mui-palette-action-hover)"
					: "transparent",
				"& .selection-checkbox": {
					opacity: bulkSelection?.isSelectionMode ? 1 : 0,
					transition: "opacity 0.2s ease-in-out",
					"&.selected": {
						opacity: 1,
					},
				},
			}}>
			<CardActionArea
				{...(href ? { component: RouterLink, href } : { onClick: handleClick })}
				disabled={loading}
				sx={{
					transform: "scale(1)",
					transition: "transform 0.1s ease-in-out",
					"&.Mui-disabled": {
						pointerEvents: "none",
					},
				}}>
				<Stack
					direction="row"
					spacing={{ xs: 1, sm: 1.5, md: 2 }}
					sx={{
						alignItems: "center",
						flex: "1 1 auto",
						p: { xs: 0.75, sm: 1, md: 1 },
					}}>
					{/* Thumbnail - Square with Overlay Checkbox */}
					<Box
						sx={{
							width: { xs: 64, sm: 80, md: 96 },
							height: { xs: 64, sm: 80, md: 96 },
							borderRadius: 2,
							overflow: "hidden",
							bgcolor: "var(--mui-palette-background-level1)",
							flexShrink: 0,
							backgroundImage: `url(${map.thumbnailUrl ?? fallbackImage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
							backgroundRepeat: "no-repeat",
							position: "relative",
						}}>
						{/* Selection Checkbox - Overlay */}
						{bulkSelection && bulkSelection.canSelectMap(map) && (
							<Box
								sx={{
									position: "absolute",
									top: { xs: 4, sm: 6, md: 8 },
									left: { xs: 4, sm: 6, md: 8 },
									zIndex: 2,
								}}>
								<Checkbox
									onClick={(e) => {
										e.stopPropagation();
									}}
									checked={bulkSelection.isMapSelected(map.id)}
									onChange={handleCheckboxChange}
									onKeyDown={handleCheckboxKeyDown}
									size="small"
									className={`selection-checkbox${bulkSelection.isMapSelected(map.id) ? " selected" : ""}`}
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.9)",
										backdropFilter: "blur(4px)",
										borderRadius: 1,
										"&:hover": {
											bgcolor: "rgba(255, 255, 255, 1)",
										},
										"& .MuiSvgIcon-root": {
											fontSize: "1.1rem",
										},
									}}
								/>
							</Box>
						)}
					</Box>

					{/* Content */}
					<Stack
						spacing={{ xs: 1, sm: 1 }}
						sx={{ flex: 1, minWidth: 0 }}>
						{/* Title Row with Inline Actions on Mobile */}
						<Stack
							direction="row"
							spacing={1}
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
							}}>
							<Typography
								variant="subtitle1"
								component="h3"
								sx={{
									fontWeight: { xs: 500, sm: 600 },
									fontSize: { xs: "0.875rem", sm: "1rem" }, // subtitle2 size on mobile, subtitle1 size on desktop
									lineHeight: 1.3,
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: 1,
									overflow: "hidden",
									textOverflow: "ellipsis",
									flex: 1,
									minWidth: 0,
								}}>
								{map.title}
							</Typography>

							{/* Mobile Actions - Inline with Title */}
							{showActions && (
								<Stack
									direction="row"
									spacing={0.5}
									sx={{
										display: { xs: "flex", sm: "none" },
										flexShrink: 0,
										alignItems: "center",
									}}>
									<MapCardMenu
										map={map}
										onDuplicate={onDuplicate}
										onDelete={onDelete}
										size="small"
										canDelete={canDelete}
									/>
								</Stack>
							)}
						</Stack>

						{/* Description */}
						<Typography
							color="text.secondary"
							variant="body2"
							sx={{
								lineHeight: 1.4,
								display: "-webkit-box",
								WebkitBoxOrient: "vertical",
								WebkitLineClamp: { xs: 1, sm: 1 },
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}>
							{map.description}
						</Typography>

						{/* Metadata chips */}
						{canDelete && (
							<Stack
								direction="row"
								spacing={1}
								sx={{
									alignItems: "center",
									flexWrap: "wrap",
									gap: 1,
								}}>
								<AccessModeIndicator
									accessMode={map.privacy}
									size="small"
								/>
								<MapAccessSettingsIndicators
									accessMode={map.privacy}
									size="small"
									shownInPortal={map.shownInPortal}
									searchEngineIndexingAllowed={map.searchEngineIndexingAllowed}
								/>
								{map.isEditableByAnyGroup && (
									<Tooltip title="Editable by authorised users in enabled groups.">
										<Chip
											icon={<Users size={14} />}
											label="Group Editable"
											color="info"
											size="small"
											variant="outlined"
											sx={metadataChipSx}
										/>
									</Tooltip>
								)}
								<Tooltip
									title={`Created by ${map.createdBy} on ${formatDate(map.createdDate)}`}>
									<Chip
										icon={<CreatorIcon size={14} />}
										label={formatOwnerShort(map.createdBy)}
										size="small"
										variant="outlined"
										sx={metadataChipSx}
									/>
								</Tooltip>
								<Tooltip title={`Last updated: ${formatDate(map.lastUpdated)}`}>
									<Chip
										icon={<CalendarIcon size={14} />}
										label={formatDateShort(map.lastUpdated)}
										size="small"
										variant="outlined"
										sx={metadataChipSx}
									/>
								</Tooltip>
							</Stack>
						)}
					</Stack>

					{/* Desktop Actions - Hidden on Mobile */}
					{showActions && (
						<Stack
							direction="row"
							spacing={1}
							sx={{
								display: { xs: "none", sm: "flex" },
								flexShrink: 0,
								alignItems: "center",
							}}>
							<MapCardMenu
								map={map}
								onDuplicate={onDuplicate}
								onDelete={onDelete}
								size="small"
								canDelete={canDelete}
							/>
						</Stack>
					)}
				</Stack>
			</CardActionArea>
		</Card>
	);
}

export function MapList({
	maps,
	onMapClick,
	onMapDuplicate,
	onMapDelete,
	loading = false,
	showStats = true,
	canDelete = () => false,
}: MapListProps): React.JSX.Element {
	const { t } = useTranslation();

	if (maps.length === 0) {
		return (
			<Box
				sx={{
					textAlign: "center",
					py: 8,
					px: 3,
				}}>
				<MapIcon
					size={48}
					color="var(--mui-palette-text-disabled)"
				/>
				<Typography
					variant="h6"
					color="text.secondary"
					sx={{ mt: 2, mb: 1 }}>
					{t("portal.results.emptyTitle")}
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary">
					{t("portal.results.emptyGuidance")}
				</Typography>
			</Box>
		);
	}

	return (
		<Stack spacing={1}>
			{maps.map((map) => (
				<MapListItem
					key={map.id}
					map={map}
					href={map.href}
					onClick={onMapClick}
					onDuplicate={onMapDuplicate}
					onDelete={onMapDelete}
					loading={loading}
					showStats={showStats}
					canDelete={canDelete(map)}
				/>
			))}
		</Stack>
	);
}
